import { Component, EventEmitter, Input, OnChanges, OnInit, Output, TemplateRef, ViewChild, ChangeDetectionStrategy, inject } from '@angular/core';
import { Grid as InavistaGrid, GridConfig, GridRowCommitEvent } from '@inavista/components/grid';
import { shipmentOrderStatuses } from 'projects/service/src/app/modules/home/models/orders.model';
import { CommonService } from 'projects/service/src/app/shared/services/common.service';
import { exportInavistaGrid } from 'projects/service/src/app/shared/utils/inavista-grid-export';
import { LocalRowsGridApi, provideLocalGridRows } from 'projects/service/src/app/shared/utils/local-rows-grid-api';
import { CellEdit, NumberEditor } from 'projects/service/src/app/shared/utils/cell-editors';

import { MatDialog } from '@angular/material/dialog';
import { MasterItemcodePopupComponent } from 'projects/shared/src/lib/components/master-itemcode-popup/master-itemcode-popup.component';
import { buildShippedItemsGridConfig, SHIPPED_ITEMS_COLUMNS, SHIPPED_ITEMS_OPEN_FIELD } from './shipped-items.grid.config';

// The old grid had no height (its gridHeight was never given a value) and showed every row. No paging now (Martin):
// the grid is as tall as its rows, up to 20, then scrolls. Toolbar + header + horizontal scrollbar, and the row height.
const GRID_CHROME_PX = 92;
const ROW_PX = 30;
const MAX_VISIBLE_ROWS = 20;
const MIN_VISIBLE_ROWS = 2;

@Component({
    selector: 'app-shipped-items',
    templateUrl: './shipped-items.component.html',
    styleUrls: ['./shipped-items.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
    // The grid reads this.receipts, not an endpoint.
    providers: [...provideLocalGridRows()]
})
export class ShippedItemsComponent implements OnInit, OnChanges {

  gridHeight = GRID_CHROME_PX + MIN_VISIBLE_ROWS * ROW_PX;
  allowEditing: boolean = true;
  gridConfig: GridConfig = null;

  @Input() receipts : any = [];
  @Input() status : string;
  @Output() onShippedItemsUpdated = new EventEmitter();

  @ViewChild('shipmentOrderReceiptsGrid') shippedItemsGrid: InavistaGrid;
  @ViewChild('qtyEditor', { static: true }) qtyEditor: TemplateRef<unknown>;
  roleCanEdit: boolean = false;
  canEdit: boolean = false;
  itemQuantityObject: NumberEditor;
  // Serviceable Received Qty's editor (CellEditorHost), and the editors open in the row being edited.
  readonly qtyEdit: CellEdit = this.initItemQuantity();
  readonly openEditors = new Map<string, CellEdit>();
  private readonly gridRows = inject(LocalRowsGridApi);

  constructor(
    private commonService: CommonService,
    private dialog: MatDialog
  ) { }

  showMasterItemCodePopup(eqData: any) {
    if (!eqData?.itemCode && !eqData?.item) return;
    const dialogRef = this.dialog.open(MasterItemcodePopupComponent, {
      panelClass: 'master-itemcode-popup-dialog',
      width: '80%',
      height: '80%',
      data: eqData
    });
    dialogRef.afterClosed().subscribe((result) => { });
  }

  ngOnChanges(change) {
    if(change?.status){
      this.status === change.status.currentValue;
      this.getCanEditValue();
      // this.enableDisableButton();
    }
    if (change?.receipts && !change.receipts.firstChange) {
      this.numberRows();
      this.refreshRows();
    }
  }

  ngOnInit(): void {
    this.numberRows();
    this.gridRows.columns.set(LocalRowsGridApi.schema(SHIPPED_ITEMS_COLUMNS));
    this.gridConfig = buildShippedItemsGridConfig(
      !!this.commonService.roleClaims['AllGrid_Toolbar_Excel_Export']?.visible, this.qtyEditor, () => this.allowEditing);
    this.refreshRows();
  }

  // The old grid's row key (tempRowId), set once per row.
  private numberRows() {
    (this.receipts ?? []).forEach((row, index) => {
      if (row.tempRowId === undefined) row.tempRowId = index;
    });
  }

  // The rows the grid shows: the receipts as the page holds them.
  refreshRows() {
    const receipts = Array.isArray(this.receipts) ? this.receipts : [];
    this.gridRows.rows.set(receipts.map(row => ({ ...row })));
    this.gridHeight = GRID_CHROME_PX + Math.min(Math.max(receipts.length, MIN_VISIBLE_ROWS), MAX_VISIBLE_ROWS) * ROW_PX;
    this.shippedItemsGrid?.refresh();
  }

  /** The page's own receipt a grid row stands for. */
  receiptFor(row: any) {
    return (this.receipts ?? []).find(r => r.tempRowId === row?.tempRowId) ?? row;
  }

  getCanEditValue(){
    this.roleCanEdit = this.commonService.roleClaims['Inv_Order_Receipt_Popup_Receipt_Tab']?.edit
    this.canEdit = this.status === shipmentOrderStatuses.deliveryAcknowledged && this.roleCanEdit;
    this.allowEditing = this.canEdit;
    // The lock changed: rows open only while the user may change them.
    if (this.gridConfig) this.gridConfig = { ...this.gridConfig, editMode: this.allowEditing ? 'inline' : 'none' };
  }

  // Leaving the row always saves it, as the Syncfusion grid did (its editor starts at 1 on an empty quantity): the row
  // counts as changed from the start, so closing it always commits or reverts.
  onRowEditStart() {
    const marked = this.shippedItemsGrid?.setDraftValue(SHIPPED_ITEMS_OPEN_FIELD, String(Date.now()));
    if (!marked) setTimeout(() => this.shippedItemsGrid?.setDraftValue(SHIPPED_ITEMS_OPEN_FIELD, String(Date.now())));
  }

  // Enter, the ✓ icon or a click elsewhere: the old actionComplete('save') with the editor's value.
  onRowCommit(commit: GridRowCommitEvent) {
    const rowData = this.receiptFor(commit.row);
    if (!rowData) return;
    const data = { ...rowData };
    for (const [field, edit] of this.openEditors) data[field] = edit.read();
    rowData.quantityServiceable = data.quantityServiceable;
    let qtyDiff = data.quantityShippedInInventoryUnit - data.quantityServiceable;
    if(qtyDiff<0){
      rowData.shortShipment = 0;
      rowData.overShipment = Math.abs(qtyDiff);
    } else if(qtyDiff>0){
      rowData.shortShipment = qtyDiff;
      rowData.overShipment = 0;
    } else if(qtyDiff===0) {
      rowData.shortShipment = 0;
      rowData.overShipment = 0;
    }
    this.refreshRows();
    this.onShippedItemsUpdated.emit(this.receipts)
  }

  // Esc or ✕: the row stays as it was (Syncfusion's cancel).
  onRowRevert() {
    this.refreshRows();
  }

  // Fixed (Martin, 2026-10-28): the old Excel button showed (AllGrid_Toolbar_Excel_Export) but its toolbarClick was
  // empty, so it did nothing; it now exports the rows shown, as every other grid's Excel button.
  onExportRequested() {
    exportInavistaGrid(this.shippedItemsGrid, 'Shipped Items');
  }

  initItemQuantity(): CellEdit {
    return {
      read: () => this.itemQuantityObject?.value,
      destroy: () => this.itemQuantityObject?.destroy(),
      open: args => {
        return this.itemQuantityObject = new NumberEditor({
          value: args.rowData.quantityServiceable || 1,
          min: 0,
          step: 1,
          decimals: 0,
        });
      }
    }
  }

  getData(){
    return this.receipts;
  }

  getItemsData(){
    return {
      itemList: (this.receipts as any).map((item, index) => {
        item.warehouseOrderLN = item.warehouseOrderLN || item.partsOrderRef;
        return item;
      })
    }
  }

}
